/**
 * useImageFetching - 画像取得用カスタムフック
 * Phase 2: AbortController統合・非同期制御・エラーハンドリング強化
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { ImageData } from '../../types/gallery';
import { ImageFetchResponse, ImageFetchError } from '../../types/api';
import { validateRoomId } from '../../utils/validation/roomValidation';

interface UseImageFetchingOptions {
  urlBase: string;
  onError?: (error: ImageFetchError) => void;
  onSuccess?: (images: ImageData[]) => void;
}

interface UseImageFetchingReturn {
  fetchImagesWithControl: (roomId?: string) => Promise<ImageFetchResponse>;
  loading: boolean;
  cancelFetching: () => void;
  isRequestActive: boolean;
}

/**
 * 画像取得用カスタムフック
 * AbortController による重複リクエスト制御と詳細なエラーハンドリングを提供
 */
export const useImageFetching = (options: UseImageFetchingOptions): UseImageFetchingReturn => {
  const { urlBase, onError, onSuccess } = options;
  
  const [loading, setLoading] = useState(false);
  const [isRequestActive, setIsRequestActive] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);
  const lastRequestTimeRef = useRef<number>(0);
  
  // デバウンス設定（連続リクエスト防止）
  const DEBOUNCE_MS = 100; // 100ms以内の連続リクエストを防ぐ（短縮）

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * 現在のリクエストをキャンセルする
   */
  const cancelFetching = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('[useImageFetching] Cancelling current request');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
      setIsRequestActive(false);
    }
  }, []);

  /**
   * APIエラーを分類して適切なエラーオブジェクトを生成
   */
  const classifyError = useCallback((error: any): ImageFetchError => {
    if (axios.isCancel(error)) {
      return {
        type: 'unknown',
        message: 'リクエストがキャンセルされました',
        retryable: false
      };
    }

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const responseData = error.response?.data as any;
      const message = responseData?.message || error.message;
      
      switch (status) {
        case 401:
          return {
            type: 'server',
            message: '認証エラーが発生しました',
            details: 'ログインしなおしてください',
            retryable: false,
            statusCode: status
          };
        case 404:
          return {
            type: 'server',
            message: 'ルームが見つかりません',
            details: 'ルームIDを確認してください',
            retryable: false,
            statusCode: status
          };
        case 500:
          return {
            type: 'server',
            message: 'サーバーエラーが発生しました',
            details: 'しばらく時間をおいて再試行してください',
            retryable: true,
            statusCode: status
          };
        case undefined:
        case 0:
          return {
            type: 'network',
            message: 'ネットワークエラーが発生しました',
            details: 'インターネット接続を確認してください',
            retryable: true
          };
        default:
          return {
            type: 'server',
            message: '画像の読み込みに失敗しました',
            details: `エラーコード: ${status} - ${message}`,
            retryable: true,
            statusCode: status
          };
      }
    }

    return {
      type: 'unknown',
      message: '予期しないエラーが発生しました',
      details: error instanceof Error ? error.message : '不明なエラー',
      retryable: true
    };
  }, []);

  /**
   * レスポンスデータを ImageData 形式に変換
   */
  const transformResponseData = useCallback((responseData: any[]): ImageData[] => {
    try {
      if (!Array.isArray(responseData)) {
        throw new Error('Invalid response format: expected array');
      }

      const transformedData = responseData.map((img: any, index: number) => {
        try {
          // 必須フィールドの検証
          const hasValidId = img.pictureid && (typeof img.pictureid === 'number' || typeof img.pictureid === 'string');
          const hasValidBase64 = img.base64 && typeof img.base64 === 'string' && img.base64.length > 0;
          
          if (!hasValidId || !hasValidBase64) {
            console.warn(`Invalid image data at index ${index}:`, {
              id: img.pictureid,
              base64Length: img.base64?.length || 0
            });
            return null;
          }

          return {
            id: img.pictureid,
            base64: img.base64,
            imageUrl: `data:image/png;base64,${img.base64}`,
            isModerated: img.isModerated ?? 0,
            selected: false,
          };
        } catch (itemError) {
          console.error(`Error processing image item ${index}:`, itemError);
          return null;
        }
      }).filter(Boolean) as ImageData[];
      
      return transformedData;
      
    } catch (error) {
      console.error('Data transformation failed:', error);
      throw error;
    }
  }, []);

  /**
   * AbortController付きで画像を取得する
   */
  const fetchImagesWithControl = useCallback(async (explicitRoomId?: string): Promise<ImageFetchResponse> => {
    console.log('[useImageFetching] Starting fetchImagesWithControl');
    
    // デバウンス：短時間での連続リクエストを防ぐ
    const now = Date.now();
    if (now - lastRequestTimeRef.current < DEBOUNCE_MS) {
      console.log('[useImageFetching] Request debounced');
      return {
        success: false,
        images: [],
        totalCount: 0,
        failedCount: 0,
        errors: [{ error: 'Request debounced', retryable: true }]
      };
    }
    lastRequestTimeRef.current = now;

    // 既存リクエストのキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const currentRequestId = ++requestIdRef.current;

    // roomId検証
    let roomId: string;
    if (explicitRoomId) {
      roomId = explicitRoomId;
    } else {
      const validation = validateRoomId();
      if (!validation.isValid) {
        const error: ImageFetchError = {
          type: 'roomId',
          message: validation.error!,
          details: 'ルーム参加画面からやり直してください',
          retryable: false
        };
        
        if (onError) {
          onError(error);
        }
        
        return {
          success: false,
          images: [],
          totalCount: 0,
          failedCount: 0,
          errors: [{ error: error.message, retryable: error.retryable }]
        };
      }
      roomId = validation.roomId!;
    }

    setLoading(true);
    setIsRequestActive(true);


    try {
      const fullApiUrl = `${urlBase}/api/picture/fetchAllImages?roomId=${roomId}`;
      console.log('[useImageFetching] Making API request to:', fullApiUrl);
      const token = localStorage.getItem("token");
      
      const response = await axios.get(fullApiUrl, { 
        headers: {
          Authorization: `Bearer ${token}`
        },
        signal: controller.signal,
        timeout: 30000 // 30秒タイムアウト

      });
      
      console.log('[useImageFetching] API response received:', {
        status: response.status,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A'
      });

      // リクエストがキャンセルされていないかチェック
      if (controller.signal.aborted) {
        throw new axios.Cancel('Request cancelled');
      }

      // 最新のリクエストかどうかチェック（競合状態対策）
      if (currentRequestId !== requestIdRef.current) {
        return {
          success: false,
          images: [],
          totalCount: 0,
          failedCount: 0,
          errors: [{ error: 'Outdated request', retryable: false }]
        };
      }
      
      // レスポンスデータの変換
      const images = transformResponseData(response.data);
      
      // 成功コールバック実行
      if (onSuccess) {
        onSuccess(images);
      }


      return {
        success: true,
        images: response.data, // 元のレスポンス形式を保持
        totalCount: images.length,
        failedCount: 0
      };

    } catch (error) {
      // リクエストがキャンセルされた場合
      if (axios.isCancel(error)) {
        throw error; // キャンセルエラーは上位に投げる
      }

      const classifiedError = classifyError(error);
      
      if (onError) {
        onError(classifiedError);
      }

      return {
        success: false,
        images: [],
        totalCount: 0,
        failedCount: 1,
        errors: [{ 
          error: classifiedError.message, 
          retryable: classifiedError.retryable 
        }]
      };

    } finally {
      // 現在のリクエストの場合のみ状態をクリア
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setIsRequestActive(false);
        
        // AbortControllerをクリア
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    }
  }, [urlBase, onError, onSuccess, classifyError, transformResponseData]);

  return {
    fetchImagesWithControl,
    loading,
    cancelFetching,
    isRequestActive
  };
};